import React from 'react'
import clsx from 'clsx'
import Badge from '../../common/badge/Badge'
import { BadgeColor } from '../../common/badge/badgeTypes'
import type { ScoreType, ScoreValue } from '../types'

type Props = {
    scoreType: ScoreType
    value: ScoreValue
}

export default function RubricScoreBadge({
    scoreType,
    value,
}: Props): React.JSX.Element {
    if (value === null || value === undefined) {
        return (
            <span className="text-gray-300 text-xs" title="Not scored">
                --
            </span>
        )
    }

    // Cross-cutting score states (apply to all score types)
    if (value === 'not_asked') {
        return <Badge color={BadgeColor.Neutral}>Not Asked</Badge>
    }
    if (value === 'not_answered') {
        return <Badge color={BadgeColor.Hold}>Not Answered</Badge>
    }

    // Binary scores: compact checkmark or X
    if (scoreType === 'binary') {
        if (value === 'pass') {
            return (
                <Badge color={BadgeColor.Success} title="Yes">
                    &#10003;
                </Badge>
            )
        }
        return (
            <Badge color={BadgeColor.Error} title="No">
                &#10005;
            </Badge>
        )
    }

    // Pass / Flag / Fail text badges
    if (value === 'pass') {
        return <Badge color={BadgeColor.Success}>Pass</Badge>
    }
    if (value === 'flag') {
        return <Badge color={BadgeColor.WarningAmber}>Flag</Badge>
    }
    if (value === 'fail') {
        return <Badge color={BadgeColor.Error}>Fail</Badge>
    }

    // Numeric 1-3: dot indicators with label
    if (typeof value === 'number' && value >= 1 && value <= 3) {
        const config =
            value === 3
                ? {
                      filled: 'bg-emerald-500',
                      label: 'Strong',
                  }
                : value === 2
                  ? {
                        filled: 'bg-amber-400',
                        label: 'Clear',
                    }
                  : {
                        filled: 'bg-red-400',
                        label: 'Touched',
                    }

        return (
            <div
                className="inline-flex items-center gap-1.5"
                title={`${config.label} (${value}/3)`}
            >
                <div className="inline-flex items-center gap-[3px]">
                    {[1, 2, 3].map((dot) => (
                        <span
                            key={dot}
                            className={clsx(
                                'w-2 h-2 rounded-full transition-colors',
                                dot <= value ? config.filled : 'bg-gray-200',
                            )}
                        />
                    ))}
                </div>
            </div>
        )
    }

    return (
        <span className="text-gray-300 text-xs" title="Not scored">
            --
        </span>
    )
}
